import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { IAppState } from 'core/store/store';
import styles from './PackageFeatures.module.scss';
import { Accordion, Button, ButtonType, ModalTypes, Spinner, StandardModal } from '@digicert/dcone-common-ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown } from '@fortawesome/pro-solid-svg-icons';
import { faExternalLinkSquare } from '@fortawesome/pro-regular-svg-icons';
import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons';
import { IAccessScope } from 'reducers/Reducers.interfaces';
import { renderManagerIcon, translate } from 'shared/helpers/utils';
import { tlmPackages, packageTypes } from './PackageFeatures.constants';
import { IPackageDetailsProps, IPackageType, IActivePackage, ICategory, IFeature } from './PackageFeatures.types';
import { CONTACT_US_URL } from 'core/utils/constants';
import { fetchPackageDetails, addPackage } from '../../Tenants.actions';
import { ApplicationKeyMapping } from '../Tenant.types';

export const PackageFeatures = ({ profile, match, applications, fetchPackageDetails, addPackage }: IPackageDetailsProps) => {
  const [packageTypesState, setPackageTypesState] = useState<IPackageType[]>(packageTypes);
  const [activePackages, setActivePackages] = useState<IActivePackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<IPackageType | null>(null);
  const { id: accountId, applicationId } = match.params;
  const appCode = applications.find((application) => application.id === applicationId)?.app_code || '';
  const columnCount = (packageTypesState?.length || 0) + 1;
  const columnStyle = { gridTemplateColumns: `repeat(${columnCount}, 1fr)` };

  useEffect(() => {
    fetchPackageDetails(accountId).then((response) => {
      setActivePackages(response.packages);
    })
  }, []);

  useEffect(() => {
    const updatedPackageTypes = packageTypes
      .map((pkg: IPackageType) => ({
        ...pkg,
        active: activePackages.some(
          (activePkg: IActivePackage) =>
            activePkg.application_code === appCode &&
            activePkg.package_code.includes(pkg.package_name)
        ),
      }))
      .filter((pkg: IPackageType, _, arr) => {
        const activePackageType = arr.find((p) => p.active);
        return activePackageType
          ? activePackageType.canUpgradeTo.includes(pkg.package_id) || pkg.active
          : true
      })
    setPackageTypesState(updatedPackageTypes)
  }, [profile, appCode, activePackages])

  const add = (): void => {
    setLoading(true);
    addPackage(
      accountId,
      applicationId,
      `${ApplicationKeyMapping[appCode]}_${selectedPackage?.package_name}`
    ).then(() => {
      setSelectedPackage(null);
      fetchPackageDetails(accountId).then((updatedResponse) => {
        setActivePackages(updatedResponse.packages);
        setLoading(false);
      })
    })
  };

  const getAppIcon = (): string => {
    const app = applications.find((application) => application.app_code === appCode);
    return app ? app.icon : '';
  };

  const getButton = (pkg: IPackageType) => {
    if (profile.access_scope === IAccessScope.account) {
      if (!pkg.active) {
        return (
          <a href={CONTACT_US_URL} target="_blank" rel="noopener noreferrer" className={styles.link}>
            <Button buttonType={ButtonType.SECONDARY}>
              {translate('dashboardLicense.packageFeatures.buttons.contactSales')}{' '}
              <FontAwesomeIcon className={styles.linkIcon} icon={faExternalLinkSquare} />
            </Button>
          </a>
        );
      } else {
        return (
          <Button buttonType={ButtonType.SECONDARY} disabled>
            {translate('dashboardLicense.packageFeatures.buttons.currentPlan')}
          </Button>
        );
      }
    } else if (profile.access_scope === IAccessScope.system) {
      if (!activePackages.some((activePkg: IActivePackage) => activePkg.application_code === appCode)) {
        return (
          <Button buttonType={ButtonType.SECONDARY} onClick={() => setSelectedPackage(pkg)}>
            {translate('dashboardLicense.packageFeatures.buttons.addPackage')}
          </Button>
        );
      } else if (pkg.active) {
        return (
          <Button buttonType={ButtonType.SECONDARY} disabled>
            {translate('dashboardLicense.packageFeatures.buttons.currentPlan')}
          </Button>
        );
      } else {
        return (
          <Button buttonType={ButtonType.SECONDARY} disabled>
            {translate('dashboardLicense.packageFeatures.buttons.upgradePackage')}
          </Button>
        );
      }
    }
    return null;
  };

  return (
    <section className={styles.packageTable}>
      <h3 className={styles.packageHeading}>
        {translate(`dashboardLicense.packageFeatures.applications.${appCode}`)}&nbsp;<span>{translate('dashboardLicense.packageFeatures.packages')}</span>
      </h3>
      {/* === Top Row for 'Current Package' Labels === */}
      <div className={styles.currentPackageRow} style={columnStyle}>
        <div /> {/* empty top-left cell */}
        {packageTypesState?.map((pkg, idx) => (
          <div key={idx} className={pkg.active ? styles.currentPackage : ''}>
            {pkg.active && translate('dashboardLicense.packageFeatures.currentPackage')}
          </div>
        ))}
      </div>
      {/* Header Row */}
      <div className={styles.header} style={columnStyle}>
        <div className={styles.title}>
          <div className={styles.appDetails}>
            <i className={styles.appIcon}>
              {renderManagerIcon(
                getAppIcon(),
                translate(`dashboardLicense.packageFeatures.applications.${appCode}`)
              )}
            </i>
            <p className={styles.app}>
              {translate(`dashboardLicense.packageFeatures.applications.${appCode}`)}
            </p>
          </div>
        </div>
        {packageTypesState?.map((pkg: IPackageType) => (
          <div key={pkg.package_id} className={styles.packageCard}>
            <div className={styles.packageName}>
              <div>{translate(`dashboardLicense.packageFeatures.${pkg.package_name}`)}</div>
              {pkg.package_name === 'premium' && (
                <FontAwesomeIcon icon={faCrown} className={styles.crownIcon} />
              )}
            </div>
            <div className={styles.packageDesc}>
              {translate(`dashboardLicense.packageFeatures.description1.${pkg.package_name}`)}
            </div>
            <div className={styles.packageDesc}>
              {translate(`dashboardLicense.packageFeatures.description2.${pkg.package_name}`)}
            </div>
            <div className={styles.actionContainer}>{getButton(pkg)}</div>
          </div>
        ))}
      </div>
      {tlmPackages.map((cat: ICategory, index: number) => (
        <Accordion
          key={cat.categoryId}
          oneByOne={false}
          defaultActiveKey={index === 0 ? cat.categoryId : ''}
          accordionData={[
            {
              headline: translate(`dashboardLicense.packageFeatures.tlmCategories.${cat.categoryId}`) as string,
              key: cat.categoryId,
              view: (
                <div>
                  {cat.features.map((feature: IFeature) => (
                    <div
                      key={feature.featureId}
                      className={styles.featureRow}
                      style={columnStyle}
                    >
                      <div className={styles.featureName}>{translate(`dashboardLicense.packageFeatures.tlmPackageFeatures.${feature.featureId}`)}</div>
                      {packageTypesState.map((level: IPackageType) => (
                        <div key={level.package_id}>
                          {typeof feature[level.package_name] === 'boolean' && feature[level.package_name] === true ?
                            <FontAwesomeIcon icon={faCheckCircle} className={styles.checkIcon} /> :
                            typeof feature[level.package_name] !== 'boolean' ? feature[level.package_name] : ''}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      ))}
      {loading && <Spinner />}
      <StandardModal
        modalType={ModalTypes.STANDARD}
        headline={translate('dashboardLicense.packageFeatures.addPackage') as string}
        display={selectedPackage ? true : false}
        buttonLabel={translate('dashboardLicense.packageFeatures.buttons.addPackage') as string}
        buttonCancelLabel={translate('common.form.buttons.cancelBtn') as string}
        onOkCallBack={add}
        onCancelCallBack={() => setSelectedPackage(null)}
      >
        <p>{translate('dashboardLicense.packageFeatures.addPackageDescription')}</p>
      </StandardModal>
    </section>
  );
};

const mapStateToProps = (state: IAppState) => ({
  profile: state.profile.data,
  applications: state.userApplications.data
});

const mapDispatchToProps = {
  fetchPackageDetails,
  addPackage
};

export default connect(mapStateToProps, mapDispatchToProps)(PackageFeatures);